import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent } from '../../../components/ui/dialog';
import {
  Bold, Italic, Heading, List, ListOrdered, Link as LinkIcon, FileSignature,
  Sparkles, Lock, Unlock, Check, X, Plus, Trash2, Upload, Printer, FileText,
  Paperclip, Users,
} from 'lucide-react';
import { toast } from 'sonner';

// Tiny markdown → HTML renderer for the preview pane (no external dep).
function renderMarkdown(md) {
  if (!md) return '';
  let h = md
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  // headings
  h = h.replace(/^### (.*)$/gm, '<h3 class="text-sm font-bold text-[#2D1B4E] mt-3">$1</h3>');
  h = h.replace(/^## (.*)$/gm, '<h2 class="text-base font-bold text-[#2D1B4E] mt-4">$1</h2>');
  h = h.replace(/^# (.*)$/gm, '<h1 class="text-lg font-bold text-[#2D1B4E] mt-4">$1</h1>');
  // bold + italic
  h = h.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  h = h.replace(/\*(.+?)\*/g, '<em>$1</em>');
  // links
  h = h.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer" class="text-[#6B3FA0] underline">$1</a>');
  // unordered lists
  h = h.replace(/^(?:[-*] .*(?:\n|$))+/gm, (block) => {
    const items = block.trim().split('\n').map(l => l.replace(/^[-*] /, '')).map(t => `<li>${t}</li>`).join('');
    return `<ul class="list-disc pl-5 my-2 text-[#2D1B4E] space-y-0.5">${items}</ul>`;
  });
  // ordered lists
  h = h.replace(/^(?:\d+\. .*(?:\n|$))+/gm, (block) => {
    const items = block.trim().split('\n').map(l => l.replace(/^\d+\. /, '')).map(t => `<li>${t}</li>`).join('');
    return `<ol class="list-decimal pl-5 my-2 text-[#2D1B4E] space-y-0.5">${items}</ol>`;
  });
  // paragraphs (split on blank lines, ignore lines already containing tags)
  h = h.split(/\n{2,}/).map(p => p.match(/^<(h\d|ul|ol|li)/) ? p : `<p class="text-sm text-[#2D1B4E] my-1.5">${p.replace(/\n/g, '<br/>')}</p>`).join('');
  return h;
}

function fmtBytes(b) {
  if (!b) return '0B';
  if (b < 1024) return `${b}B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)}KB`;
  return `${(b / 1024 / 1024).toFixed(1)}MB`;
}

const STATUS_BADGE = {
  draft: { label: 'Draft', bg: '#F0EDF6', fg: '#5A4D6E', border: '#DED8EB' },
  pending_approval: { label: 'Pending Approval', bg: '#FFF3E0', fg: '#E65100', border: '#FFB74D' },
  approved: { label: 'Approved · Locked', bg: '#E8F5E9', fg: '#1B5E20', border: '#43A047' },
};

export default function MinutesEditor({ open, onClose, api, meeting, members, isAdmin, onSaved }) {
  // Local state mirrors the meeting so we don't lose edits on rerender
  const [text, setText] = useState('');
  const [decisions, setDecisions] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [actualAttendees, setActualAttendees] = useState([]);
  const [status, setStatus] = useState('draft');
  const [approvedBy, setApprovedBy] = useState('');
  const [approvedAt, setApprovedAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [decisionText, setDecisionText] = useState('');
  const [decisionAssignee, setDecisionAssignee] = useState('');
  const [decisionDue, setDecisionDue] = useState('');
  const [decisionMakesAction, setDecisionMakesAction] = useState(false);

  const memberName = (id) => members.find(m => m.id === id)?.name || '—';
  const locked = status === 'approved';

  useEffect(() => {
    if (!meeting) return;
    setText(meeting.minutes || '');
    setDecisions(meeting.minutes_decisions || []);
    setAttachments(meeting.minutes_attachments || []);
    setActualAttendees(meeting.actual_attendee_member_ids || []);
    setStatus(meeting.minutes_status || 'draft');
    setApprovedBy(meeting.minutes_approved_by || '');
    setApprovedAt(meeting.minutes_approved_at || '');
  }, [meeting]);

  const insertMd = (before, after = before) => {
    const ta = document.getElementById('mn-editor');
    if (!ta) return;
    const s = ta.selectionStart, e = ta.selectionEnd;
    const newText = text.slice(0, s) + before + text.slice(s, e) + after + text.slice(e);
    setText(newText);
    setTimeout(() => { ta.focus(); ta.selectionStart = s + before.length; ta.selectionEnd = e + before.length; }, 0);
  };
  const insertLine = (prefix) => {
    const ta = document.getElementById('mn-editor');
    if (!ta) return;
    const s = ta.selectionStart;
    // find line start
    let lineStart = s;
    while (lineStart > 0 && text[lineStart - 1] !== '\n') lineStart--;
    const newText = text.slice(0, lineStart) + prefix + text.slice(lineStart);
    setText(newText);
    setTimeout(() => { ta.focus(); ta.selectionStart = ta.selectionEnd = s + prefix.length; }, 0);
  };

  const autofillFromAgenda = () => {
    const items = meeting?.agenda || [];
    if (items.length === 0) { toast.info('No agenda items to autofill from'); return; }
    if (text && !window.confirm('Replace current minutes with agenda-based template?')) return;
    const template = items.map((a, i) => `## ${i + 1}. ${a.title || 'Item'}${a.presenter ? ` — ${a.presenter}` : ''}\n\n*Notes:* \n`).join('\n');
    setText(template);
  };

  const saveAll = async () => {
    if (locked) { toast.error('Minutes are locked. Unlock to edit.'); return; }
    setBusy(true);
    try {
      // Note: minutes_attachments are managed server-side via dedicated
      // /minutes-attachments endpoints (upload + delete) so we don't send
      // them here — would otherwise overwrite the storage-path entries with
      // a stale client copy.
      await api.put(`/board/meetings/${meeting.id}`, {
        minutes: text,
        actual_attendee_member_ids: actualAttendees,
      });
      toast.success('Minutes saved');
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail?.message || 'Failed'); }
    setBusy(false);
  };

  const addDecision = async () => {
    if (!decisionText.trim()) { toast.error('Decision text required'); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/board/meetings/${meeting.id}/minutes/decisions`, {
        text: decisionText.trim(),
        create_action_item: decisionMakesAction,
        assigned_to_member_id: decisionAssignee,
        due_date: decisionDue,
      });
      setDecisions(prev => [...prev, data]);
      setDecisionText(''); setDecisionAssignee(''); setDecisionDue(''); setDecisionMakesAction(false);
      toast.success(decisionMakesAction ? 'Decision logged + action item created' : 'Decision logged');
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail?.message || 'Failed'); }
    setBusy(false);
  };

  const delDecision = async (did) => {
    if (!window.confirm('Remove this decision?')) return;
    try {
      await api.delete(`/board/meetings/${meeting.id}/minutes/decisions/${did}`);
      setDecisions(prev => prev.filter(d => d.id !== did));
      onSaved?.();
    } catch (e) { toast.error('Failed'); }
  };

  const handleAttach = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (f.size > 100 * 1024 * 1024) { toast.error('Max 100MB'); return; }
    if (!meeting?.id) { toast.error('Save the meeting first before attaching files'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const { data: att } = await api.post(`/board/meetings/${meeting.id}/minutes-attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
      setAttachments(prev => [...prev, att]);
      toast.success('Attached');
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    setBusy(false);
  };

  const downloadAttachment = async (a) => {
    if (a.storage_path) {
      try {
        const resp = await api.get(`/board/meetings/${meeting.id}/minutes-attachments/${a.id}`, { responseType: 'blob' });
        const blob = new Blob([resp.data], { type: a.mime || 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url; link.download = a.name; link.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } catch { toast.error('Download failed'); }
      return;
    }
    // Legacy inline attachment
    if (a.b64) {
      const link = document.createElement('a');
      link.href = a.b64; link.download = a.name; link.click();
    }
  };

  const removeAttachment = async (id) => {
    const att = attachments.find(a => a.id === id);
    if (!att) return;
    // Server-side delete for storage-backed attachments
    if (att.storage_path && meeting?.id) {
      try {
        await api.delete(`/board/meetings/${meeting.id}/minutes-attachments/${id}`);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Could not remove');
        return;
      }
    }
    setAttachments(prev => prev.filter(a => a.id !== id));
    onSaved?.();
  };

  const toggleAttendee = (id) => {
    setActualAttendees(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const submitForApproval = async () => {
    if (!window.confirm('Submit minutes for approval? You will need an admin to approve.')) return;
    setBusy(true);
    try {
      await api.post(`/board/meetings/${meeting.id}/minutes/submit`, {});
      setStatus('pending_approval');
      toast.success('Submitted for approval');
      onSaved?.();
    } catch (e) { toast.error('Failed'); }
    setBusy(false);
  };

  const approve = async () => {
    if (!window.confirm('Approve and lock these minutes? Further changes require unlock.')) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/board/meetings/${meeting.id}/minutes/approve`, {});
      setStatus('approved'); setApprovedBy(data.minutes_approved_by); setApprovedAt(data.minutes_approved_at);
      toast.success('Minutes approved');
      onSaved?.();
    } catch (e) { toast.error('Failed'); }
    setBusy(false);
  };

  const unlock = async () => {
    if (!window.confirm('Unlock approved minutes to edit?')) return;
    setBusy(true);
    try {
      await api.post(`/board/meetings/${meeting.id}/minutes/unlock`, {});
      setStatus('draft'); setApprovedBy(''); setApprovedAt('');
      toast.success('Unlocked');
      onSaved?.();
    } catch (e) { toast.error('Failed'); }
    setBusy(false);
  };

  const exportPdf = () => {
    const attendList = (actualAttendees || []).map(memberName).join(', ');
    const decisionHtml = (decisions || []).map((d, i) => `<li>${d.text}${d.action_item_id ? ' <em style="color:#6B3FA0">→ Action item created</em>' : ''}</li>`).join('');
    const renderedMinutes = renderMarkdown(text);
    const html = `<!doctype html><html><head><title>${meeting.title} — Minutes</title>
      <style>
        body{font-family:Georgia,serif;max-width:760px;margin:30px auto;padding:0 24px;color:#222;line-height:1.5}
        h1{color:#6B3FA0;border-bottom:2px solid #6B3FA0;padding-bottom:6px;margin-bottom:8px}
        h2,h3{color:#2D1B4E;margin-top:18px;margin-bottom:6px}
        .meta{color:#5A4D6E;font-size:13px;margin-bottom:18px}
        .status{display:inline-block;padding:3px 10px;border-radius:12px;font-size:11px;font-weight:bold;text-transform:uppercase;letter-spacing:.5px}
        .approved{background:#E8F5E9;color:#1B5E20;border:1px solid #43A047}
        .draft{background:#F0EDF6;color:#5A4D6E;border:1px solid #DED8EB}
        .pending{background:#FFF3E0;color:#E65100;border:1px solid #FFB74D}
        .sig{margin-top:36px;border-top:1px solid #DED8EB;padding-top:10px;font-size:12px;color:#5A4D6E;font-style:italic}
      </style></head><body>
      <h1>${meeting.title}</h1>
      <div class="meta">
        <strong>Date:</strong> ${meeting.meeting_date} ${meeting.time_start || ''}${meeting.time_end ? `–${meeting.time_end}` : ''}<br/>
        <strong>Location:</strong> ${meeting.location || '—'}<br/>
        <strong>Status:</strong> <span class="status ${status}">${(STATUS_BADGE[status] || {}).label || status}</span>
      </div>
      <h2>Attendees</h2>
      <p>${attendList || '<em>None recorded</em>'}</p>
      <h2>Minutes</h2>
      <div>${renderedMinutes || '<em>No minutes recorded.</em>'}</div>
      ${decisions.length ? `<h2>Decisions Made</h2><ol>${decisionHtml}</ol>` : ''}
      ${approvedBy ? `<div class="sig">Approved by <strong>${approvedBy}</strong> on ${(approvedAt || '').slice(0, 19).replace('T', ' ')}</div>` : ''}
      <script>window.onload=()=>setTimeout(()=>window.print(),200);</script>
    </body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); }
  };

  if (!meeting) return null;
  const badge = STATUS_BADGE[status] || STATUS_BADGE.draft;
  const canEdit = isAdmin && !locked;
  const canSubmit = !locked && status !== 'pending_approval';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto p-0" data-testid="minutes-editor">
        <div className="p-5 border-b border-[#F0EDF6] bg-[#F7F4EF]">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-lg font-bold text-[#2D1B4E]"><FileSignature className="h-5 w-5 inline mr-1 text-[#6B3FA0]" /> Minutes — {meeting.title}</h2>
              <p className="text-xs text-[#5A4D6E]">{meeting.meeting_date} {meeting.time_start && `· ${meeting.time_start}`} {meeting.location && `· ${meeting.location}`}</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge style={{ background: badge.bg, color: badge.fg, borderColor: badge.border }} className="text-xs font-bold uppercase tracking-wider" data-testid="minutes-status-badge">
                {locked && <Lock className="h-3 w-3 inline mr-1" />} {badge.label}
              </Badge>
              {approvedBy && <span className="text-[10px] text-[#8E85A0]">by {approvedBy}</span>}
              <Button size="sm" variant="outline" onClick={exportPdf} className="h-8 text-xs" data-testid="export-pdf-btn"><Printer className="h-3 w-3 mr-1" /> Export PDF</Button>
              <Button size="sm" variant="ghost" onClick={onClose} className="h-8 text-xs"><X className="h-4 w-4" /></Button>
            </div>
          </div>
          {/* Workflow actions */}
          <div className="mt-3 flex flex-wrap gap-2 items-center">
            {canSubmit && (
              <Button size="sm" onClick={submitForApproval} disabled={busy} className="bg-[#FFB74D] hover:bg-[#FFA726] h-8 text-xs" data-testid="submit-approval-btn">
                <FileText className="h-3 w-3 mr-1" /> Submit for Approval
              </Button>
            )}
            {isAdmin && status === 'pending_approval' && (
              <Button size="sm" onClick={approve} disabled={busy} className="bg-[#43A047] hover:bg-[#388E3C] h-8 text-xs" data-testid="approve-btn">
                <Check className="h-3 w-3 mr-1" /> Approve & Lock
              </Button>
            )}
            {isAdmin && locked && (
              <Button size="sm" onClick={unlock} disabled={busy} className="bg-[#FFB74D] hover:bg-[#FFA726] h-8 text-xs" data-testid="unlock-btn">
                <Unlock className="h-3 w-3 mr-1" /> Unlock
              </Button>
            )}
            {canEdit && (
              <Button size="sm" onClick={saveAll} disabled={busy} className="bg-[#6B3FA0] hover:bg-[#5A2F8F] h-8 text-xs ml-auto" data-testid="save-minutes-btn">
                {busy ? 'Saving…' : 'Save Changes'}
              </Button>
            )}
          </div>
        </div>

        <div className="p-5 grid lg:grid-cols-2 gap-5">
          {/* LEFT: Editor */}
          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs uppercase tracking-wider text-[#5A4D6E] font-bold">Minutes Body</Label>
                <Button size="sm" variant="ghost" onClick={autofillFromAgenda} disabled={locked} className="h-6 text-[10px]" data-testid="autofill-btn">
                  <Sparkles className="h-3 w-3 mr-1 text-[#6B3FA0]" /> Auto-fill from agenda
                </Button>
              </div>
              {/* Toolbar */}
              <div className="flex flex-wrap gap-1 mb-1 p-1 bg-[#F7F4EF] border border-[#DED8EB] rounded">
                <Tool icon={Bold} onClick={() => insertMd('**')} disabled={locked} test="md-bold" />
                <Tool icon={Italic} onClick={() => insertMd('*')} disabled={locked} test="md-italic" />
                <Tool icon={Heading} onClick={() => insertLine('## ')} disabled={locked} test="md-h" />
                <Tool icon={List} onClick={() => insertLine('- ')} disabled={locked} test="md-ul" />
                <Tool icon={ListOrdered} onClick={() => insertLine('1. ')} disabled={locked} test="md-ol" />
                <Tool icon={LinkIcon} onClick={() => insertMd('[', '](url)')} disabled={locked} test="md-link" />
              </div>
              <textarea id="mn-editor" value={text} onChange={e => setText(e.target.value)} disabled={locked} rows={16}
                className="w-full border border-[#DED8EB] rounded p-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#9B7ED8] disabled:bg-[#F7F4EF] disabled:text-[#8E85A0]"
                placeholder="Markdown is supported. Use the toolbar buttons or click 'Auto-fill from agenda' to get started."
                data-testid="minutes-textarea" />
            </div>

            {/* Attachments */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5A4D6E] font-bold flex items-center gap-1">
                <Paperclip className="h-3 w-3" /> Supporting Files ({attachments.length})
              </Label>
              <div className="space-y-1 mt-1">
                {attachments.map(a => (
                  <div key={a.id} className="flex items-center justify-between bg-[#F7F4EF] border border-[#F0EDF6] rounded px-2 py-1.5 text-xs" data-testid={`attachment-${a.id}`}>
                    <button onClick={() => downloadAttachment(a)} className="flex items-center gap-1.5 text-[#2D1B4E] hover:underline truncate">
                      <FileText className="h-3 w-3 text-[#6B3FA0]" /> {a.name}
                      <span className="text-[10px] text-[#8E85A0]">({fmtBytes(a.size)})</span>
                    </button>
                    {canEdit && <button onClick={() => removeAttachment(a.id)} className="text-[#D9534F]"><Trash2 className="h-3 w-3" /></button>}
                  </div>
                ))}
                {canEdit && (
                  <label className="flex items-center justify-center gap-1.5 border-2 border-dashed border-[#DED8EB] rounded p-2 cursor-pointer hover:border-[#9B7ED8] text-xs text-[#5A4D6E]">
                    <Upload className="h-3 w-3 text-[#6B3FA0]" /> Attach a file (≤8MB)
                    <input type="file" onChange={handleAttach} className="hidden" data-testid="minutes-attach-input" />
                  </label>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT: Preview + decisions + attendance */}
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5A4D6E] font-bold">Live Preview</Label>
              <div className="border border-[#DED8EB] rounded p-3 bg-white min-h-[300px] max-h-[400px] overflow-y-auto"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(text) || '<em class="text-[#8E85A0] text-sm">No content yet.</em>' }}
                data-testid="minutes-preview" />
            </div>

            {/* Actual attendance */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5A4D6E] font-bold flex items-center gap-1">
                <Users className="h-3 w-3" /> Actual Attendance ({actualAttendees.length}/{members.length})
              </Label>
              <div className="max-h-32 overflow-y-auto border border-[#DED8EB] rounded p-2 mt-1 space-y-0.5 bg-white">
                {members.map(m => {
                  const wasExpected = (meeting.attendee_member_ids || []).includes(m.id);
                  const attended = actualAttendees.includes(m.id);
                  return (
                    <label key={m.id} className="flex items-center justify-between text-xs cursor-pointer hover:bg-[#F7F4EF] px-1 py-0.5 rounded" data-testid={`attendance-${m.id}`}>
                      <div className="flex items-center gap-2">
                        <input type="checkbox" checked={attended} onChange={() => toggleAttendee(m.id)} disabled={locked} className="h-3 w-3 accent-[#43A047]" />
                        <span className="text-[#2D1B4E]">{m.name}</span>
                      </div>
                      <span className="flex items-center gap-1">
                        {wasExpected && <Badge className="text-[9px] bg-[#EDE7F6] text-[#6B3FA0]">Invited</Badge>}
                        {attended && !wasExpected && <Badge className="text-[9px] bg-[#FFF3E0] text-[#E65100]">Unscheduled</Badge>}
                        {!attended && wasExpected && <Badge className="text-[9px] bg-[#FDECEA] text-[#D9534F]">Absent</Badge>}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Decisions log */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5A4D6E] font-bold">Decisions ({decisions.length})</Label>
              <div className="space-y-1.5 mt-1">
                {decisions.map(d => (
                  <div key={d.id} className="bg-[#EDE7F6]/40 border border-[#9B7ED8]/40 rounded p-2 text-xs" data-testid={`decision-${d.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-[#2D1B4E] font-medium">{d.text}</p>
                      {canEdit && <button onClick={() => delDecision(d.id)} className="text-[#D9534F] shrink-0"><Trash2 className="h-3 w-3" /></button>}
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-[10px] text-[#8E85A0]">
                      {d.action_item_id && <Badge className="text-[9px] bg-[#E8F5E9] text-[#1B5E20]">→ Action item</Badge>}
                      {d.created_by && <span>by {d.created_by}</span>}
                    </div>
                  </div>
                ))}
              </div>
              {canEdit && (
                <div className="mt-2 border border-dashed border-[#DED8EB] rounded p-2 bg-[#F7F4EF] space-y-1.5">
                  <Input value={decisionText} onChange={e => setDecisionText(e.target.value)} placeholder="New decision (e.g. Approved 2026 budget of $1.4M)" className="text-xs h-8" data-testid="new-decision-input" />
                  <label className="flex items-center gap-1.5 text-[10px] cursor-pointer text-[#5A4D6E]">
                    <input type="checkbox" checked={decisionMakesAction} onChange={e => setDecisionMakesAction(e.target.checked)} className="h-3 w-3" data-testid="makes-action-check" />
                    Also create an action item from this decision
                  </label>
                  {decisionMakesAction && (
                    <div className="grid grid-cols-2 gap-1">
                      <select value={decisionAssignee} onChange={e => setDecisionAssignee(e.target.value)} className="border border-[#DED8EB] rounded px-1 py-0.5 text-xs bg-white">
                        <option value="">Assign to…</option>
                        {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                      </select>
                      <Input type="date" value={decisionDue} onChange={e => setDecisionDue(e.target.value)} className="text-xs h-7" />
                    </div>
                  )}
                  <Button size="sm" onClick={addDecision} disabled={busy || !decisionText.trim()} className="bg-[#6B3FA0] hover:bg-[#5A2F8F] h-7 text-xs w-full" data-testid="add-decision-btn">
                    <Plus className="h-3 w-3 mr-1" /> Log Decision
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Tool({ icon: Icon, onClick, disabled, test }) {
  return (
    <button onClick={onClick} disabled={disabled} className="p-1.5 rounded hover:bg-[#EDE7F6] disabled:opacity-30" data-testid={test}>
      <Icon className="h-3.5 w-3.5 text-[#6B3FA0]" />
    </button>
  );
}
